import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import Loader from "../common/Loader";

const getApiBase = () =>
  (process.env.REACT_APP_LOCAL_API || "http://localhost:4200/auth/v1").replace(
    /\/auth\/v1\/?$/,
    ""
  );

const statusBadgeClass = (status) => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "FAILED":
      return "failed";
    case "REVERSED":
      return "reversed";
    case "PENDING":
      return "pending";
    default:
      return "pending";
  }
};

const ShowSirRewards = ({
  showModal,
  closeModal,
  userId,
  userToken,
  canReverse = true,
}) => {
  const [rewards, setRewards] = useState([]);
  const [totalSirRewards, setTotalSirRewards] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [reversingId, setReversingId] = useState(null);

  const loadRewards = useCallback(() => {
    if (!userId || !userToken) return;
    setIsLoading(true);
    axios
      .get(`${getApiBase()}/v2/reward/list/${userId}`, {
        headers: { Authorization: `Bearer ${userToken}` },
      })
      .then((response) => {
        setRewards(response.data?.rewards || []);
        setTotalSirRewards(response.data?.totalSirRewards || 0);
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          text:
            error?.response?.data?.message || "Failed to load SIR rewards",
          icon: "error",
          confirmButtonColor: "#3ca151",
        });
      })
      .finally(() => setIsLoading(false));
  }, [userId, userToken]);

  useEffect(() => {
    if (showModal) loadRewards();
  }, [showModal, loadRewards]);

  const handleReverse = (reward) => {
    if (!reward.sirActionId) {
      Swal.fire({
        title: "Cannot reverse",
        text: "This reward has no SIR action ID.",
        icon: "warning",
        confirmButtonColor: "#3ca151",
      });
      return;
    }

    Swal.fire({
      title: "Are you sure you want to reverse this SIR reward?",
      text: `${reward.rewardType} — ${reward.tokensDistributed || reward.amount} tokens`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#3ca151",
      cancelButtonText: "No",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setReversingId(reward.sirActionId);
      axios
        .post(
          `${getApiBase()}/v2/reward/reverse`,
          {
            actionId: reward.sirActionId,
            reason: "Admin reversed from dashboard",
            reversalPercentage: 100,
            refundIdempotencyKey: `refund_${reward.sirActionId}_${Date.now()}`,
          },
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then(() => {
          Swal.fire({
            title: "Reversed Successfully",
            text: "This SIR reward has been reversed.",
            icon: "success",
            confirmButtonColor: "#3ca151",
          });
          loadRewards();
        })
        .catch((error) => {
          Swal.fire({
            title: "Error",
            text: error?.response?.data?.message || "Failed to reverse reward",
            icon: "error",
            confirmButtonColor: "#3ca151",
          });
        })
        .finally(() => setReversingId(null));
    });
  };

  if (!showModal) return null;

  return (
    <div className="modal-overlay-formal" onClick={closeModal}>
      <div
        className="modal-sir-rewards"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sir-rewards-title"
      >
        <button
          type="button"
          className="close-button sir-rewards-close"
          onClick={closeModal}
          aria-label="Close"
        >
          x
        </button>
        <div className="sir-rewards-header">
          <h2 id="sir-rewards-title" className="sir-rewards-title">
            SIR Rewards
          </h2>
          <p className="exchange-rate sir-rewards-subtitle">
            Total completed: {totalSirRewards} tokens
          </p>
        </div>

        {isLoading ? (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <Loader />
          </div>
        ) : rewards.length === 0 ? (
          <p className="table-text" style={{ padding: "24px 0", fontSize: "14px" }}>
            No SIR rewards found for this user.
          </p>
        ) : (
          <div className="transactions-table-container">
            <table className="transactions-table" style={{ textAlign: "left" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Date</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Type</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Tokens</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Status</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Action ID</th>
                  {canReverse && (
                    <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rewards.map((reward) => (
                  <tr key={reward.id} className="transaction-row">
                    <td className="date-cell" style={{ textAlign: "left" }}>
                      {moment(reward.createdAt).format("DD-MMM-YYYY hh:mm A")}
                    </td>
                    <td className="type-cell" style={{ textAlign: "left" }}>
                      {reward.rewardType}
                    </td>
                    <td className="amount-cell" style={{ textAlign: "left" }}>
                      {reward.tokensDistributed || reward.amount}
                    </td>
                    <td className="status-cell" style={{ textAlign: "left" }}>
                      <span
                        className={`status-badge ${statusBadgeClass(reward.status)}`}
                      >
                        {reward.status}
                      </span>
                    </td>
                    <td
                      className="id-cell"
                      style={{ textAlign: "left", fontSize: "12px", wordBreak: "break-all" }}
                    >
                      {reward.sirActionId || "—"}
                    </td>
                    {canReverse && (
                      <td className="action-cell" style={{ textAlign: "left" }}>
                        {reward.status === "COMPLETED" && reward.sirActionId && (
                          <button
                            type="button"
                            className="reverse-button"
                            disabled={reversingId === reward.sirActionId}
                            onClick={() => handleReverse(reward)}
                          >
                            {reversingId === reward.sirActionId
                              ? "Reversing..."
                              : "Reverse"}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowSirRewards;
